import React, { useState, useEffect } from "react";
import BannerManagerService from "../../services/dashboard/BannerManager";
import config from "../../config";
import { UploadCloud, Trash2, PenBox, LucidePenBox } from "lucide-react";
import Select from "react-select";
import BookManager from "../../services/dashboard/BookManager";
import debounce from "lodash/debounce";

export default function BannerManager() {
  const emptyBanner = {
    bannerHeading: "",
    bannerHighlight: "",
    bannerDescription: "",
    bannerCtaBtnText: "",
    bannerCtaBtnLink: "",
    bookId: "",
    platform: null,
    position: null,
    bannerType: null,
  };

  const [banners, setBanners] = useState([]);
  const [bannerDetails, setBannerDetails] = useState(emptyBanner);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [bookOptions, setBookOptions] = useState([]);
  const [bookSearchLoading, setBookSearchLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const debouncedSearch = debounce((value) => {
    handleBookSearch(value);
  }, 300);

  const platformOptions = [
    { value: "WEB", label: "Web" },
    { value: "APP", label: "APP" },
  ];

  const positionOptions = [
    { value: "TOP", label: "Top" },
    { value: "MIDDLE", label: "MIDDLE" },
  ];

  const bannerTypeOptions = [
    { value: "WELCOME", label: "WELCOME" },
    { value: "BOOK", label: "BOOK" },
    { value: "OFFER", label: "OFFER" },
    { value: "EXPLORE", label: "EXPLORE" },
    { value: "AUTHOR", label: "AUTHOR" },
    { value: "CATEGORY", label: "CATEGORY" },
  ];

  const handleBookSearch = async (inputValue) => {
    if (!inputValue) return;
    try {
      setBookSearchLoading(true);
      const result = await BookManager.searchBooks(inputValue);
      // result here is the full response object, so extract responseData:
      const books = result.responseData || [];

      const options = books.map((book) => ({
        value: book.bookId,
        label: book.bookName, // note your API returns bookName, not bookTitle
      }));
      setBookOptions(options);
    } catch (err) {
      console.error("Error searching books", err);
    } finally {
      setBookSearchLoading(false);
    }
  };

  const handleBookSelect = (selectedOption) => {
    setSelectedBook(selectedOption);
    setBannerDetails({
      ...bannerDetails,
      bookId: selectedOption ? selectedOption.value : "",
    });
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const data = await BannerManagerService.getAllBanners();
      setBanners(data);
    } catch (err) {
      console.error("Failed to fetch banners", err);
    }
  };

  const handleInput = (e) => {
    setBannerDetails({
      ...bannerDetails,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      alert("Only JPG, PNG or WEBP allowed");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert("Max size 2MB");
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile && !isEditing) {
      alert("Image is required");
      return;
    }

    setIsSaving(true);
    try {
      const bannerToSubmit = {
        ...bannerDetails,
        bookId: bannerDetails.bookId ? Number(bannerDetails.bookId) : null,
      };

      if (isEditing && editingBannerId) {
        await BannerManagerService.updateBanner(
          editingBannerId,
          bannerToSubmit,
          imageFile
        );
        alert("Banner updated!");
      } else {
        await BannerManagerService.addBanner(bannerToSubmit, imageFile);
        alert("Banner added!");
      }

      // reset form
      setBannerDetails(emptyBanner);
      setImageFile(null);
      setPreviewUrl("");
      setSelectedBook(null);
      setBookOptions([]);
      setIsEditing(false);
      setEditingBannerId(null);
      
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";
      
      fetchBanners();
    } catch (err) {
      console.error(err);
      alert("Failed to save banner");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await BannerManagerService.deleteBanner(id);
      fetchBanners();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  const handleEdit = (banner) => {
    setIsEditing(true);
    setEditingBannerId(banner.bannerId);
    setBannerDetails({
      bannerHeading: banner.bannerHeading,
      bannerHighlight: banner.bannerHighlight,
      bannerDescription: banner.bannerDescription,
      bannerCtaBtnText: banner.bannerCtaBtnText,
      bannerCtaBtnLink: banner.bannerCtaBtnLink,
      bookId: banner.bookId || "",
      platform: banner.platform,
      position: banner.position,
      bannerType: banner.bannerType,
    });
    
    // Set selected book if available
    if (banner.bookId) {
      setSelectedBook({
        value: banner.bookId,
        label: banner.bookName || `Book ID: ${banner.bookId}`,
      });
    } else {
      setSelectedBook(null);
    }
    
    setPreviewUrl(`${config.url}/bannerImage/${banner.bannerImage}`);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto dark:bg-gray-900">
      <h1 className="text-2xl font-bold mb-4 dark:text-white">Banner Manager</h1>

      {/* ——— Form ——— */}
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-8 space-y-4 border border-primary-shade2 dark:border-gray-700"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <input
            name="bannerHeading"
            value={bannerDetails.bannerHeading}
            onChange={handleInput}
            placeholder="Heading"
            className="border rounded px-4 py-2 focus:ring-primary w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <input
            name="bannerHighlight"
            value={bannerDetails.bannerHighlight}
            onChange={handleInput}
            placeholder="Highlight"
            className="border rounded px-4 py-2 focus:ring-primary w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <input
            name="bannerCtaBtnText"
            value={bannerDetails.bannerCtaBtnText}
            onChange={handleInput}
            placeholder="CTA Button Text"
            className="border rounded px-4 py-2 focus:ring-primary w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <input
            name="bannerCtaBtnLink"
            value={bannerDetails.bannerCtaBtnLink}
            onChange={handleInput}
            placeholder="CTA Button Link"
            className="border rounded px-4 py-2 focus:ring-primary w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <Select
            placeholder="Search & Select Book (optional)"
            isClearable
            options={bookOptions}
            value={selectedBook}
            onChange={handleBookSelect}
            onInputChange={(value, { action }) => {
              if (action === "input-change") {
                debouncedSearch(value);
              }
              return value;
            }}
            isLoading={bookSearchLoading}
            className="w-full"
          />
          <Select
            placeholder="Select Platform"
            options={platformOptions}
            value={platformOptions.find(
              (option) => option.value === bannerDetails.platform
            )}
            onChange={(selected) =>
              setBannerDetails({
                ...bannerDetails,
                platform: selected ? selected.value : null,
              })
            }
          />

          <Select
            placeholder="Select Position"
            options={positionOptions}
            value={positionOptions.find(
              (option) => option.value === bannerDetails.position
            )}
            onChange={(selected) =>
              setBannerDetails({
                ...bannerDetails,
                position: selected ? selected.value : null,
              })
            }
          />

          <Select
            placeholder="Select Banner Type"
            options={bannerTypeOptions}
            value={bannerTypeOptions.find(
              (option) => option.value === bannerDetails.bannerType
            )}
            onChange={(selected) =>
              setBannerDetails({
                ...bannerDetails,
                bannerType: selected ? selected.value : null,
              })
            }
          />
        </div>

        <textarea
          name="bannerDescription"
          value={bannerDetails.bannerDescription}
          onChange={handleInput}
          placeholder="Description"
          className="border rounded px-4 py-2 focus:ring-primary w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
        />

        <div className="flex items-center gap-4">
          <label className="cursor-pointer text-primary dark:text-primary-shade2 flex items-center gap-2">
            <UploadCloud /> Upload Image
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </label>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview"
              className="w-32 h-20 object-cover rounded shadow dark:shadow-gray-700"
            />
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSaving}
            className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-shade4 transition"
          >
            {isSaving
              ? isEditing
                ? "Updating..."
                : "Saving..."
              : isEditing
              ? "Update Banner"
              : "Add Banner"}
          </button>
          
          {isEditing && (
            <button
              type="button"
              onClick={() => {
                setBannerDetails(emptyBanner);
                setImageFile(null);
                setPreviewUrl("");
                setSelectedBook(null);
                setBookOptions([]);
                setIsEditing(false);
                setEditingBannerId(null);
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) fileInput.value = "";
              }}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* ——— Grid of existing banners ——— */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
        {banners.map((b) => (
          <div
            key={b.bannerId}
            className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow dark:shadow-gray-700 relative"
          >
            <img
              src={`${config.url}/bannerImage/${b.bannerImage}`}
              alt={b.bannerHeading}
              className="w-full h-40 object-fit"
            />
            <div className="p-4">
              <h2 className="font-semibold text-lg dark:text-white">{b.bannerHeading}</h2>
              {b.bannerHighlight && (
                <p className="text-sm text-primary dark:text-primary-shade2 mt-1">{b.bannerHighlight}</p>
              )}
              <p className="text-gray-700 dark:text-gray-300 mt-2">{b.bannerDescription}</p>
              {b.bannerCtaBtnText && b.bannerCtaBtnLink && (
                <a
                  href={b.bannerCtaBtnLink}
                  className="inline-block mt-3 px-4 py-1 bg-primary text-white rounded"
                >
                  {b.bannerCtaBtnText}
                </a>
              )}
            </div>
            <button
              onClick={() => handleDelete(b.bannerId)}
              className="absolute top-2 right-2 bg-white dark:bg-gray-700 p-1 rounded-full shadow hover:bg-red-100 dark:hover:bg-red-900"
            >
              <Trash2 className="text-red-500 dark:text-red-400" />
            </button>
            <button
              onClick={() => handleEdit(b)}
              className="absolute top-2 left-2 bg-white dark:bg-gray-700 p-1 rounded-full shadow hover:bg-blue-100 dark:hover:bg-blue-900"
            >
              <LucidePenBox className="text-orange-500 dark:text-orange-400 w-6 h-6" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
